import { createRoot } from 'react-dom/client';

import './index.css';
import '@/lib/editor/tiptap.css';

import { setupRegionBasedConfig } from 'config/config';

import Router from './routes';
import { initApm } from './util/apm';
import {
	checkAuthentication,
	createClient,
	ensureTokenMatchesTenantContext,
	handleRedirectCb,
	resolveAndSetRegionCode,
	setUserData,
	syncRequestedTenantContext,
} from './util/auth/auth';
import { routerRef } from './util/routerRef';

const runMain = async () => {
	if (import.meta.env.MODE === 'test') return;

	const rootEl = document.getElementById('react-content');
	if (!rootEl) return;

	const showFallback = (message: string) => {
		const root = createRoot(rootEl);
		root.render(<div style={{ padding: 24, textAlign: 'center', fontFamily: 'sans-serif' }}>{message}</div>);
	};

	try {
		const apm = initApm();

		// Next lines deal with authentication, always needs to be the first thing that runs on the app
		const auth = await createClient();

		await handleRedirectCb(auth); // has side effects

		const isAuthenticated = await checkAuthentication(auth); // has side effects

		if (!isAuthenticated) return;

		await setUserData(auth); // has side effects

		await syncRequestedTenantContext(); // has side effects

		const refreshedUser = await ensureTokenMatchesTenantContext(auth); // has side effects

		const regionCode = await resolveAndSetRegionCode(auth); // has side effects

		window.process = window.process || ({ env: {} } as NodeJS.Process);
		window.process.env.RegionCode = regionCode;

		setupRegionBasedConfig({ regionCode }); // has side effects, interpolates {regionCode} in all string properties in the config object with the right region code

		if (refreshedUser?.sub) {
			apm.setUserContext({
				id: refreshedUser.sub,
				email: refreshedUser.email,
			});
		}

		const { createRouter } = await import('@tanstack/react-router');
		const { routeTree } = await import('./routeTree');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const router = createRouter({ routeTree } as any);
		routerRef.current = router;

		const root = createRoot(rootEl);
		root.render(<Router />);
	} catch (err) {
		console.error('App failed to start:', err);
		showFallback('Failed to load app. Check the console for errors.');
	}
};

runMain();
